import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActionButton from "../../Button/ActionButton";
import PaymentTermsDialog from "../PaymentTermsGate/PaymentTermsDialog";
import useProfessionalAccountStatus from "../../../hooks/useProfessionalAccountStatus";
import { NEW_CONFIGURATION_PATH } from "../../../utils/paymentsRoutes";

/**
 * Starts the registration of a payment account.
 *
 * Straight to the form: this button only exists on screens a professional who
 * already has an account is looking at, and what the service is was explained
 * to them before their first one. The welcome screen is the first-time path and
 * is not shown again.
 *
 * The terms come first, and only the first time: once agreed to, registering a
 * second account goes straight through. Closing the dialog leaves the
 * professional exactly where they were, since not agreeing simply means not
 * registering — nothing to confirm and nowhere to be sent.
 */
export default function RegisterAccountButton({
  variant
}: {
  variant?: "primary" | "secondary";
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { mustAgreeToPaymentTerms } = useProfessionalAccountStatus();
  const [showingTerms, setShowingTerms] = useState(false);

  const goToRegistration = useCallback(
    () => history.push(NEW_CONFIGURATION_PATH),
    [history]
  );

  const onClick = useCallback(() => {
    if (mustAgreeToPaymentTerms) {
      setShowingTerms(true);
      return;
    }
    goToRegistration();
  }, [mustAgreeToPaymentTerms, goToRegistration]);

  return (
    <>
      <ActionButton
        label={t("paymentConfigurations.register")}
        onClick={onClick}
        variant={variant}
      />
      <PaymentTermsDialog
        open={showingTerms}
        onClose={() => setShowingTerms(false)}
        onAccepted={goToRegistration}
      />
    </>
  );
}
